import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { PDFDocument } from "pdf-lib";
import { useTranslation } from "react-i18next";
import { Endpoints } from "../../Global/Endpoints";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import ErrorModal from "../Security/ErrorModal";
import "react-toastify/dist/ReactToastify.css";

import loginImage01 from "../../assets/images/registration/registration-steps-image-01.jpg";
import loginImage02 from "../../assets/images/registration/registration-steps-image-03.jpg";
import loginImage03 from "../../assets/images/registration/registration-steps-image-04.jpg";

const InsuranceUpload = () => {
  const { t } = useTranslation();
  const [hasWINBenefit, setHasWINBenefit] = useState(null);
  const [hasSecondaryInsurance, setHasSecondaryInsurance] = useState(null);
  const [hasWINBenefitSecondary, setHasWINBenefitSecondary] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [uploadedData, setUploadedData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";

  const [selectedFiles, setSelectedFiles] = useState({
    primaryFront: [],
    primaryBack: [],
    secondaryFront: [],
    secondaryBack: [],
  });

  const [selectedFileNames, setSelectedFileNames] = useState({
    primaryFront: "Choose File",
    primaryBack: "Choose File",
    secondaryFront: "Choose File",
    secondaryBack: "Choose File",
  });

  const [uploadSuccess, setUploadSuccess] = useState({
    primaryFront: false,
    primaryBack: false,
    secondaryFront: false,
    secondaryBack: false,
  });

  const [uploadButtonDisabled, setUploadButtonDisabled] = useState({
    primaryFront: false,
    primaryBack: false,
    secondaryFront: false,
    secondaryBack: false,
  });

  const [fileError, setFileError] = useState({
    primaryFront: "",
    primaryBack: "",
    secondaryFront: "",
    secondaryBack: "",
    form: "",
  });

  const token = localStorage.getItem("AccessToken") || "YOUR_ACCESS_TOKEN_HERE";
  const contract = useSelector((state) => state.userData);
  const navigate = useNavigate();

  const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];

  const compressImage = async (file) => {
    const options = { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true };
    try {
      return await imageCompression(file, options);
    } catch {
      return file;
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileSelection = async (e, type) => {
    let anyTooLarge = false;
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        anyTooLarge = true;
        return false;
      }
      return true;
    });
    if (anyTooLarge) {
      return toast.error("File size should not exceed 2MB.");
    }
    setSelectedFileNames((prev) => ({
      ...prev,
      [type]: validFiles.length > 0 ? validFiles.map((f) => f.name).join(", ") : "Choose File",
    }));

    if (validFiles.length > 0) {
      const processedFile =
        validFiles[0].type.startsWith("image") ? await compressImage(validFiles[0]) : validFiles[0];
      setSelectedFiles((prev) => ({ ...prev, [type]: [processedFile] }));
      setUploadButtonDisabled((prev) => ({ ...prev, [type]: false }));
      setUploadSuccess((prev) => ({ ...prev, [type]: false }));
      setFileError((prev) => ({ ...prev, [type]: "" }));
    } else {
      setFileError((prev) => ({ ...prev, [type]: t("selectValidFormatFile") }));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const errors = {};

    for (let key of ["primaryFront", "primaryBack"]) {
      if (!uploadSuccess[key]) {
        errors[key] = t("pleaseUploadThisDocument");
        isValid = false;
      }
    }

    if (hasSecondaryInsurance) {
      for (let key of ["secondaryFront", "secondaryBack"]) {
        if (!uploadSuccess[key]) {
          errors[key] = t("pleaseUploadThisDocument");
          isValid = false;
        }
      }
    }

    if (!isHighmarkPortal && hasWINBenefit === null) {
      errors.form = t("pleaseSelectWINBenefit");
      isValid = false;
    }

    if (hasSecondaryInsurance === null) {
      errors.form = t("pleaseSelectSecondaryInsurance");
      isValid = false;
    }

    setFileError((prev) => ({ ...prev, ...errors }));
    return isValid;
  };

  const uploadDocuments = async (files, buttonType) => {
    if (files.length === 0) {
      setFileError((prev) => ({ ...prev, [buttonType]: t("noFileSelected") }));
      return;
    }

    setIsUploading(true);
    setUploadButtonDisabled((prev) => ({ ...prev, [buttonType]: true }));

    try {
      const payloads = await Promise.all(
        files.map(async (file) => {
          let noOfPages = 1;
          if (file.type === "application/pdf") {
            const inputBytes = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(inputBytes);
            noOfPages = pdfDoc.getPageCount();
          }

          return {
            FileID: Date.now().toString(),
            FileName: file.name,
            NoOFPages: noOfPages.toString(),
            inputBytes: await fileToBase64(file),
            fileSize: file.size.toString(),
            memberId: contract.userData.MemberID,
            contract: contract.userData.ContractIdentified,
            docTypeDescription: buttonType,
            DeviceType: "PatientPortal",
            EmailID: contract.userData.EmailID,
          };
        })
      );

      const response = await axios.post(Endpoints.UploadDocument, JSON.stringify(payloads), {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      if (response.data?.length) {
        setUploadedData(response.data);
        setUploadSuccess((prev) => ({ ...prev, [buttonType]: true }));
        setFileError((prev) => ({ ...prev, [buttonType]: "" }));
        await sendToWin(response.data, buttonType);
      }
    } catch (error) {
      setFileError((prev) => ({ ...prev, [buttonType]: t("uploadError") }));
    } finally {
      setIsUploading(false);
      setUploadButtonDisabled((prev) => ({ ...prev, [buttonType]: true }));
    }
  };

  const sendToWin = async (uploadedData, buttonType) => {
    if (!uploadedData.length) return;
    const docTypeDescMap = {
      primaryFront: "First Insurance ID Front",
      primaryBack: "First Insurance ID Back",
      secondaryFront: "Second Insurance ID Front",
      secondaryBack: "Second Insurance ID Back",
    };

    const payload = {
      PhysicianName: "",
      TotalAmount: 0,
      DocumentTypes: "",
      UserName: "MAPP",
      Documents: uploadedData.map((file) => ({
        NoOFPages: file.NoOFPages,
        FilePath: file.FilePath,
        FileID: file.FileID,
        FileName: file.FileName,
      })),
      EmailID: contract.userData.EmailID,
      ReimbursementType: "",
      Activity: "",
      DoctypeDesc: docTypeDescMap[buttonType],
      Contract: contract.userData.ContractIdentified,
      AuthNo: "",
      GroupID: "",
      PhysicianTaxNo: "",
      ProviderID: "",
      Speciality: "",
      MemberID: contract.userData.MemberID,
      ServiceType: "",
      DeviceType: "PatientPortal",
      PhysicianTelNo: ""
    };

    try {
      await axios.post(Endpoints.SendToWin, payload, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      toast.error(t("uploadError"), { autoClose: 1000, position: "top-right" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      _params: JSON.stringify({
        EmailID: contract.userData.EmailID,
        winBenefit: hasWINBenefit,
        secondPlan: hasSecondaryInsurance,
        secondWinBenefit: hasWINBenefitSecondary,
      }),
    };

    try {
      const response = await axios.post(Endpoints.SaveInsuranceData, payload, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.result === 1) {
        setModalMessage("Your insurance details have been uploaded successfully!");
        setShowModal(true);
      } else {
        setModalMessage("Failed to upload insurance details");
        setShowModal(true);
      }
    } catch (error) {
      console.error("Submit failed:", error);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (modalMessage.includes("successfully")) {
      setSubmitted(true);
    }
  };

  const handleRedirect = () => navigate("/home");

  return (
    <div className="registration-block-main">
      {/* Image Carousel */}
      <div className="registration-image-wrapper">
        <div id="carouselExampleSlidesOnly" className="carousel slide" data-bs-ride="carousel">
          <div className="carousel-inner">
            {[loginImage01, loginImage02, loginImage03].map((img, idx) => (
              <div key={idx} className={`carousel-item login-carousel-item ${idx === 0 ? "active" : ""}`} data-bs-interval="5000">
                <img src={img} alt="" className="registration-image" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="registration-form-main">
        <div className="registration-form">
          {submitted ? (
            <>
              <h2 className="page-heading-bold">{t("thankyou")}</h2>
              <p>{t("thankyoumessage")}</p>
              <button onClick={handleRedirect} className="win-btn-primary mobile-view mt-3 mb-5">Go to Home</button>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="page-heading-secondary-black mb-3">{t("uploadhealthcard")}</h2>
              <p className="mb-5">{t("primarymsg")}</p>

              {["primaryFront", "primaryBack"].map((key, idx) => (
                <div key={key} className="file-upload-container mb-4 d-flex flex-column">
                  <div className="d-flex align-items-center">
                    <input type="file" id={key} className="file-input" onChange={(e) => handleFileSelection(e, key)} />
                    <label htmlFor={key} className="custom-file-label me-3">{selectedFileNames[key]}</label>
                    <button type="button" onClick={() => uploadDocuments(selectedFiles[key], key)} className="win-btn-primary mobile-view me-2" disabled={uploadButtonDisabled[key]}>
                      {t(idx === 0 ? "frontcard" : "backofcard")}
                    </button>
                    {uploadSuccess[key] && <span style={{ color: "green", fontSize: "1.5rem" }}>&#10003;</span>}
                  </div>
                  {fileError[key] && <span className="text-danger small">{fileError[key]}</span>}
                </div>
              ))}

              {/* Radio Groups */}
              {!isHighmarkPortal && (
                <>
                  <p className="mb-2">{t("secondscrn")}</p>
                  <div className="d-flex mb-3">
                    {["yes", "no"].map((val) => (
                      <label key={val} className="d-flex align-items-center me-5">
                        <input
                          type="radio"
                          name="winBenefit"
                          className="form-check-input me-2"
                          value={val}
                          onChange={() => setHasWINBenefit(val === "yes")}
                          required
                        />
                        {val.charAt(0).toUpperCase() + val.slice(1)}
                      </label>
                    ))}
                  </div>
                </>
              )}

              <p>{t("thirdscrn")}</p>
              <div className="d-flex mb-3">
                {["yes", "no"].map((val) => (
                  <label key={val} className="d-flex align-items-center me-5">
                    <input type="radio" name="secondaryInsurance" className="form-check-input me-2" value={val} onChange={() => setHasSecondaryInsurance(val === "yes")} required />
                    {val.charAt(0).toUpperCase() + val.slice(1)}
                  </label>
                ))}
              </div>

              {hasSecondaryInsurance && (
                <>
                  <p>{t("secondaycard")}</p>
                  {["secondaryFront", "secondaryBack"].map((key, idx) => (
                    <div key={key} className="file-upload-container d-flex flex-column mb-3">
                      <div className="d-flex align-items-center">
                        <input type="file" id={key} className="file-input" onChange={(e) => handleFileSelection(e, key)} />
                        <label htmlFor={key} className="custom-file-label me-3">{selectedFileNames[key]}</label>
                        <button type="button" onClick={() => uploadDocuments(selectedFiles[key], key)} className="win-btn-primary mobile-view me-2" disabled={uploadButtonDisabled[key]}>
                          {t(idx === 0 ? "frontcard" : "backofcard")}
                        </button>
                        {uploadSuccess[key] && <span style={{ color: "green", fontSize: "1.5rem" }}>&#10003;</span>}
                      </div>
                      {fileError[key] && <span className="text-danger small">{fileError[key]}</span>}
                    </div>
                  ))}

                  <p>{t("fourthscrn")}</p>
                  <div className="d-flex">
                    {["yes", "no"].map((val) => (
                      <label key={val} className="d-flex align-items-center me-5">
                        <input type="radio" name="winBenefitSecondary" className="form-check-input me-2" value={val} onChange={() => setHasWINBenefitSecondary(val === "yes")} required />
                        {val.charAt(0).toUpperCase() + val.slice(1)}
                      </label>
                    ))}
                  </div>
                </>
              )}

              {fileError.form && <p className="text-danger">{fileError.form}</p>}
              <button type="submit" className="win-btn-primary mobile-view mt-3 mb-5">{t("submit")}</button>
            </form>
          )}
        </div>
      </div>

      <ErrorModal show={showModal} message={modalMessage} onClose={handleModalClose} />
    </div>
  );
};

export default InsuranceUpload;
